/**
 * Capture a still frame from a video, in the browser.
 *
 * The Media Library grid used to show a film-strip icon for every video. A real frame is what makes
 * a video recognisable at a glance, and the browser is the only place in this system that can
 * produce one: the API runtime has no video decoder at all (its entire OS surface is openssl), and
 * putting ffmpeg in it would mean a foreign binary plus untrusted-media decoding on the same
 * half-a-vCPU replica that serves the SPA — for a 30 kB JPEG.
 *
 * Doing it here costs nothing instead. At upload time the page already holds the File, so a frame is
 * zero extra network bytes and zero server CPU; the poster rides along in the same multipart request.
 *
 * Everything is best-effort and resolves `null` rather than throwing. A poster is a nicety and an
 * upload is not — no codec, no permission, and no timeout may ever cost someone their file.
 */

/**
 * Long edge of the captured poster, in pixels.
 *
 * Grid tiles are ~320 CSS px at the widest breakpoint, so 640 covers a 2× display with nothing
 * wasted. The point of capping it at all is that this JPEG is stored per video forever.
 */
const POSTER_MAX_EDGE = 640;

/** JPEG quality. 0.8 is the usual point where artefacts stop being visible at thumbnail size. */
const POSTER_QUALITY = 0.8;

/**
 * Give up after this long.
 *
 * A non-faststart MP4 makes the browser read a long way into the file before it can decode, and a
 * corrupt one may never fire an event at all. Neither is worth waiting on — the upload is what the
 * user asked for.
 */
const TIMEOUT_MS = 5_000;

/**
 * Capture a frame from a local video File. Returns null for anything that isn't a decodable video.
 *
 * Uses an object URL, which is same-origin, so the canvas is not tainted and `toBlob` works.
 */
export async function captureVideoPoster(file: File): Promise<Blob | null> {
  if (!file.type.startsWith('video/') || file.size === 0) return null;
  const url = URL.createObjectURL(file);
  try {
    return await captureVideoPosterFromUrl(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Capture a frame from a video URL that is already loadable without tainting a canvas — in practice
 * an object URL made from a blob this app fetched itself.
 *
 * Exported for the viewer, which downloads the whole video to play it and can therefore fill in a
 * poster for an older upload from bytes that are already in the page.
 */
export async function captureVideoPosterFromUrl(url: string): Promise<Blob | null> {
  const video = document.createElement('video');
  video.muted = true;
  // iOS Safari refuses to decode into a canvas without this.
  video.playsInline = true;
  video.preload = 'metadata';

  try {
    return await new Promise<Blob | null>((resolve) => {
      let settled = false;
      const done = (blob: Blob | null) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(blob);
      };
      const timer = window.setTimeout(() => done(null), TIMEOUT_MS);

      video.onerror = () => done(null);

      video.onloadeddata = () => {
        // A tenth of the way in, capped at one second: frame zero of anything that fades in is a
        // black rectangle, which is a worse thumbnail than the icon it replaces.
        const duration = video.duration;
        video.currentTime =
          Number.isFinite(duration) && duration > 0 ? Math.min(1, duration * 0.1) : 0;
      };

      video.onseeked = () => {
        const width = video.videoWidth;
        const height = video.videoHeight;
        // Zero dimensions means there is no decodable video track — an audio file with a video
        // mimetype, or a container the browser opened but cannot render.
        if (!width || !height) {
          done(null);
          return;
        }
        const scale = Math.min(1, POSTER_MAX_EDGE / Math.max(width, height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          done(null);
          return;
        }
        try {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        } catch {
          done(null);
          return;
        }
        canvas.toBlob((blob) => done(blob), 'image/jpeg', POSTER_QUALITY);
      };

      video.src = url;
    });
  } finally {
    // Release the decoder rather than waiting for GC; a few of these during a multi-file upload
    // otherwise sit on memory holding whole videos.
    video.onerror = null;
    video.onloadeddata = null;
    video.onseeked = null;
    video.removeAttribute('src');
    video.load();
  }
}
